"use client"
import { Utils } from "@/libs/Utils"
import dynamic from "next/dynamic"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import React from "react"
import { Button, Col, Form, FormControl, InputGroup, Row, Spinner } from "react-bootstrap"

const PerformTestBreakdown = dynamic(() => import("./PerformTestBreakdown")) as any
const PerformTestItem = dynamic(() => import("./PerformTestItem")) as any

const PerformTest: React.FC<{ t?: any, pid?: any }> = ({ t, pid }) => {
	const [result, setPerformResult] = React.useState<any>()
	const [performTimeout, setPerformTimeout] = React.useState<any>()

	const initialPerformTestWithId = async (pid: string) => {
		const res = await fetch(`/api/hubble/${pid}`)
		const { data } = await res.json()

		if (data) {
			setPerformResult(data)
			clearTimeout(performTimeout)

			if (data?.status === "done") return [
				setPerformTimeout(null)
			]

			setPerformTimeout(setTimeout(() => initialPerformTestWithId(pid), 3000))
		}
	}

	React.useEffect(() => {
		if (pid) {
			initialPerformTestWithId(pid)
		}

		return () => performTimeout && clearTimeout(performTimeout)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<>
			<div className="speedtest-one--result bg-light p-5 rounded">
				{(result) ? (
					<>
						<Row>
							<Col md={6} sx={12}>
								<p className="small fw-bold">Crawled information</p>
								<p className="mb-1 d-flex small"><span className="my-auto">ID: {pid}</span></p>
								<p className="mb-1 d-flex small"><span className="my-auto">Status: {t(`form.perform.status.${result.status}`)}</span></p>
							</Col>
							<Col md={6} sx={12}>
								<p className="small fw-bold">Detailed information</p>
								<p className="mb-1 d-flex small"><i className="my-auto flag small me-1" /> <span className="my-auto">{result.url}</span> {result.redirect && (`🠒 ${result.redirect}`)}</p>
								<p className="mb-1 d-flex small"><i className="my-auto event-note small me-1" /> <span className="my-auto">{new Date(result.timestamp * 1000).toString()}</span></p>
								<p className="mb-0 d-flex small"><i className="my-auto near-me small me-1" /> <span className="my-auto">{result.countries.length} locations</span></p>
							</Col>
						</Row>
						<div className="mt-4 border-top">
							<p className="mt-3 mb-3 small fw-bold">Summary</p>
							<Row>
								{result.status !== "done" && (
									<Col sx={12}>
										<Spinner animation="border" style={{ width: 24, height: 24, borderWidth: '0.2rem' }} />
									</Col>
								)}
								{result.fastest && <PerformTestItem name="The fastest" data={result.fastest} />}
								{result.slowest && <PerformTestItem name="The slowest" data={result.slowest} />}
								{result.average && <PerformTestItem name="Average load time" data={result.average} />}
							</Row>
						</div>
						<div className="mt-4 border-top">
							<p className="mt-3 mb-3 small fw-bold">Breakdown countries</p>
							<Row>
								<Col sx={12}>
									<PerformTestBreakdown results={result.countries} />
								</Col>
							</Row>
						</div>
					</>
				) : (
					<Spinner animation="border" style={{ width: 24, height: 24, borderWidth: '0.2rem' }} />
				)}
			</div>
			<style jsx>{`
					.speedtest-one--result {
						color: #333333;
					}
			`}</style>
		</>
	)
}
interface SpeedtestOneProps {
	locale: string
	setting?: any
}

const SpeedtestOne: React.FC<SpeedtestOneProps> = ({ locale, setting = {} }) => {

	const pathname = usePathname()
	const search = useSearchParams()
	const router = useRouter()
	const t = new Utils(locale).Translation
	const [validated, setValidated] = React.useState<boolean>(false)
	const [performTestId, setPerformTestId] = React.useState<any>(undefined)

	const handleSubmit = async (event: any) => {
		[
			event.preventDefault(),
			event.stopPropagation()
		]

		if (event.currentTarget.checkValidity()) {
			const { website } = event.target.elements

			const response = await fetch('/api/hubble/0', {
				method: 'POST',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ "website": website?.value })
			})

			const { data } = await response.json()

			if (data?.id) {
				router.push(`${pathname}?pid=${data?.id}`);
				setPerformTestId(data?.id)
			}
		}

		return [
			setValidated(true)
		]
	}

	React.useEffect(() => {
		if (search.get('pid')) {
			setPerformTestId(search.get('pid'))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<>
			<div className="mt-5 d-flex h-100" data-ui="module.ui:SpeedtestOne">
				<div className="w-100 my-auto">
					{(performTestId) ? (
						<PerformTest t={t} pid={performTestId} />
					) : (
						<div className="row">
							<div className="col-md-6 col-12">
								<Form noValidate validated={validated} onSubmit={handleSubmit}>
									<InputGroup className="mb-2">
										<FormControl required pattern="^(http|https)://((?!-))(xn--)?[a-z0-9][a-z0-9-_]{0,61}[a-z0-9]{0,1}\.(xn--)?([a-z0-9\-]{2,61}|[a-z0-9-]{1,30}\.[a-z]{2,})$" type="text" name="website" placeholder={setting?.placeholder || "Enter a web page URL"} />
										< Button className="btn" type="submit">{t("form.speedtest.submit")}</Button>
									</InputGroup>
								</Form>
							</div>
						</div>
					)}
				</div>
			</div>
		</>
	)
}

export default SpeedtestOne